import React, { FC } from "react";
import { Container } from "../../../../components";
import HorizontalRule from "../../../../components/components/atoms/HorizontalRule";
import { Box, Heading, Text } from "@telia/teddy";
import _groupBy from "lodash/groupBy";
import { AdditionalProductVariant, Discount } from "../../../../../../models";
import WebdealColorPicker from "../WebdealColorPickerV2/WebdealColorPickerV2";

import "./WebdealList.less";

interface WebdeaListProps {
    selectedVariants: AdditionalProductVariant[];
    selectedVariantsByFamily: Record<string, AdditionalProductVariant>[];
    setSelectedVariants: (selectedVariants: AdditionalProductVariant[]) => void;
    setSelectedVariantsByFamily: (v: Record<string, AdditionalProductVariant>[]) => void;
    webdeals: Discount[];
}

const WebdeaList: FC<WebdeaListProps> = ({
    webdeals,
    selectedVariants,
    setSelectedVariants,
    selectedVariantsByFamily,
    setSelectedVariantsByFamily,
}) => {
    const handleSetSelectedVariant = (webdealIndex: number, variant?: AdditionalProductVariant) => {
        if (variant) {
            const newSelectedVariants = [...selectedVariants];
            newSelectedVariants[webdealIndex] = variant;
            setSelectedVariants(newSelectedVariants);
        }
    };

    const handleSetSelectedFamilyVariant = (
        webdealIndex: number,
        modelSlug: string,
        variant: AdditionalProductVariant,
    ) => {
        const updated = [...selectedVariantsByFamily];
        updated[webdealIndex] = { ...updated[webdealIndex], [modelSlug]: variant };
        setSelectedVariantsByFamily(updated);
    };

    return (
        <Container className="webdeal-wrapper" noPadding>
            {webdeals.map((webdeal, i) => {
                if (webdeal.multipleOptionsOneChoicePerFamily) {
                    const groupedByModelSlug = _groupBy(webdeal.additionalProducts.variants, "modelSlug");
                    return (
                        <Container key={webdeal.code} className="webdeal-container" whiteBg>
                            <div className="webdeal-container__heading">
                                <Heading className="webdeal-container__webdeal-name" as="h2">
                                    {webdeal.discountName}
                                </Heading>
                            </div>
                            <Text dangerouslySetInnerHTML={{ __html: webdeal.descriptionInCheckout }} />
                            <div className="webdeal-container__product-wrapper">
                                <div className="webdeal-container__family-groups">
                                    {Object.entries(groupedByModelSlug).map(([modelSlug, familyVariants]) => {
                                        const selectedFamilyVariant =
                                            selectedVariantsByFamily[i]?.[modelSlug] ?? familyVariants[0];
                                        const groupedByModel = _groupBy(familyVariants, "model");
                                        return (
                                            <div key={modelSlug} className="webdeal-container__family-group">
                                                <Heading
                                                    mb="100"
                                                    className="webdeal-container__family-group-header"
                                                    variant="subsection-100"
                                                    as="h3"
                                                >
                                                    Velg ønsket variant:
                                                </Heading>
                                                {!!selectedFamilyVariant.originalSalesPrice && (
                                                    <div className="webdeal-container__price-wrapper">
                                                        <span className="webdeal-container__price--strikethrough">{`${selectedFamilyVariant.originalSalesPrice},-`}</span>
                                                        <span className="webdeal-container__price--discount">{`${selectedFamilyVariant.discountedPrice},-`}</span>
                                                    </div>
                                                )}
                                                <div className="webdeal-container__product">
                                                    {selectedFamilyVariant.image?.url && (
                                                        <div className="webdeal-container__product__imageContainer">
                                                            <img
                                                                className="webdeal-container__product__image"
                                                                src={selectedFamilyVariant.image?.url}
                                                                alt=""
                                                            />
                                                        </div>
                                                    )}
                                                    <div>
                                                        {Object.entries(groupedByModel).map(([model, variants]) => {
                                                            const shouldShowSelectedColor =
                                                                selectedFamilyVariant.model === model;
                                                            return (
                                                                <WebdealColorPicker
                                                                    key={model}
                                                                    title={model}
                                                                    colors={variants.map((variant, index) => ({
                                                                        id: index,
                                                                        html: variant.htmlColor,
                                                                        dedicatedColor: variant.colorName,
                                                                    }))}
                                                                    selectedColor={
                                                                        shouldShowSelectedColor
                                                                            ? {
                                                                                  html: selectedFamilyVariant.htmlColor,
                                                                                  dedicatedColor:
                                                                                      selectedFamilyVariant.colorName,
                                                                              }
                                                                            : undefined
                                                                    }
                                                                    setSelectedColor={(color) => {
                                                                        const matched = familyVariants.find(
                                                                            (v) =>
                                                                                v.colorName === color.dedicatedColor &&
                                                                                v.model === model,
                                                                        );
                                                                        if (matched) {
                                                                            handleSetSelectedFamilyVariant(
                                                                                i,
                                                                                modelSlug,
                                                                                matched,
                                                                            );
                                                                        }
                                                                    }}
                                                                    id={`${webdeal.code}-${modelSlug}`}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Box mt="300">
                                    <Text className="webdeal-container__chosen-products-label">Valgte produkter:</Text>
                                    <ul className="webdeal-container__chosen-products-list">
                                        {Object.entries(groupedByModelSlug).map(([modelSlug, familyVariants]) => {
                                            const selected =
                                                selectedVariantsByFamily[i]?.[modelSlug] ?? familyVariants[0];
                                            return (
                                                <li key={modelSlug}>
                                                    <Text>
                                                        {selected.model} {selected.colorName}
                                                    </Text>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </Box>
                            </div>
                        </Container>
                    );
                }

                const groupedByModel = _groupBy(webdeal.additionalProducts.variants, "model");
                return (
                    <Container key={webdeal.code} className="webdeal-container" whiteBg>
                        <div className="webdeal-container__heading">
                            <Heading className="webdeal-container__webdeal-name" as="h2">
                                {webdeal.discountName}
                            </Heading>
                            {!!selectedVariants[i].originalSalesPrice && (
                                <div className="webdeal-container__price-wrapper">
                                    <span className="webdeal-container__price--strikethrough">{`${selectedVariants[i].originalSalesPrice},-`}</span>
                                    <span className="webdeal-container__price--discount">{`${selectedVariants[i].discountedPrice},-`}</span>
                                </div>
                            )}
                        </div>
                        <div className="webdeal-container__product-wrapper">
                            <Text dangerouslySetInnerHTML={{ __html: webdeal.descriptionInCheckout }} />
                            <div className="webdeal-container__product">
                                {selectedVariants[i].image?.url && (
                                    <div className="webdeal-container__product__imageContainer">
                                        <img
                                            className="webdeal-container__product__image"
                                            src={selectedVariants[i].image?.url}
                                            alt=""
                                        />
                                    </div>
                                )}
                                <div>
                                    {Object.entries(groupedByModel).map(([model, variants]) => {
                                        const shouldShowSelectedColor = selectedVariants[i].model === model;
                                        return (
                                            <WebdealColorPicker
                                                key={model}
                                                title={model}
                                                colors={variants.map((variant, index) => ({
                                                    id: index,
                                                    html: variant.htmlColor,
                                                    dedicatedColor: variant.colorName,
                                                }))}
                                                selectedColor={
                                                    shouldShowSelectedColor
                                                        ? {
                                                              html: selectedVariants[i].htmlColor,
                                                              dedicatedColor: selectedVariants[i].colorName,
                                                          }
                                                        : undefined
                                                }
                                                setSelectedColor={(color): void => {
                                                    handleSetSelectedVariant(
                                                        i,
                                                        webdeal.additionalProducts.variants.find(
                                                            (variant) =>
                                                                variant.colorName === color.dedicatedColor &&
                                                                variant.model === model,
                                                        ),
                                                    );
                                                }}
                                                id={webdeal.code}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                            <HorizontalRule />
                            <Box mt="200">
                                <Text>
                                    Valgt produkt: {selectedVariants[i].model} {selectedVariants[i].colorName}
                                </Text>
                            </Box>
                        </div>
                    </Container>
                );
            })}
        </Container>
    );
};

export default WebdeaList;
